import {
  LuChartLine,
  LuContact,
  LuCreditCard,
  LuFlag,
  LuGauge,
  LuGitBranch,
  LuImages,
  LuLayoutGrid,
  LuLayoutTemplate,
  LuMegaphone,
  LuPalette,
  LuPenTool,
  LuPhone,
  LuSquareUser,
  LuStore,
  LuUsers,
  LuWorkflow,
} from 'react-icons/lu';
import { ROUTE_PATHS } from '../../routes/path-map';

/**
 * Workspace nav — prototype 09b / TV_SAAS.workspaceNav (Create · Library · Insights · Account · Admin).
 * `icon` is a Lucide (react-icons/lu) component rendered as an inline SVG.
 * Each item's `roles` array controls visibility — must stay in sync with route guards.
 */
export const SIDEBAR_SECTIONS = [
  {
    id: 'create',
    titleKey: 'Create',
    showSectionHeader: true,
    items: [
      {
        id: 'dashboard',
        to: '/dashboard',
        icon: LuLayoutGrid,
        labelKey: 'Dashboard',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'campaigns',
        to: '/campaigns',
        icon: LuMegaphone,
        labelKey: 'Campaigns',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'templates',
        to: '/templates',
        icon: LuLayoutTemplate,
        labelKey: 'Templates',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'editor',
        action: 'createDesign',
        icon: LuPenTool,
        labelKey: 'Editor',
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'workflows',
        to: ROUTE_PATHS.workflows,
        icon: LuGitBranch,
        labelKey: 'Workflows',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
        featureKey: 'workflows',
      },
    ],
  },
  {
    id: 'library',
    titleKey: 'Library',
    showSectionHeader: true,
    items: [
      {
        id: 'assets',
        to: '/assets',
        // Tag Manager is a tab of the same page (AssetsTagsTabs), so its route keeps this item active.
        activePaths: [ROUTE_PATHS.manageTags],
        icon: LuImages,
        labelKey: 'Assets & Tags',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'actors',
        to: '/actors',
        icon: LuSquareUser,
        labelKey: 'Actors',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'contacts',
        to: ROUTE_PATHS.contacts,
        icon: LuPhone,
        labelKey: 'Contacts',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'crm',
        to: '/crm',
        icon: LuStore,
        labelKey: 'CRM & Shopify',
        crownTitle: 'CRM — Pay as you go',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        featureKey: 'shopify',
        premiumLocked: true,
        freeHidden: false,
      },
      {
        id: 'trustvid-crm',
        to: ROUTE_PATHS.trustvidCrm,
        icon: LuContact,
        labelKey: 'TrustVid CRM',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        featureKey: 'trustvid_crm',
        freeHidden: true,
      },
    ],
  },
  {
    id: 'insights',
    titleKey: 'Insights',
    showSectionHeader: true,
    items: [
      {
        id: 'video-analytics',
        to: '/video-analytics',
        icon: LuChartLine,
        labelKey: 'Video Analytics',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        premiumLocked: true,
      },
      {
        id: 'metering',
        to: '/metering',
        icon: LuGauge,
        labelKey: 'Usage & metering',
        crownTitle: 'Metering — Pay as you go',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        premiumLocked: true,
      },
    ],
  },
  {
    id: 'account',
    titleKey: 'Account',
    showSectionHeader: true,
    items: [
      {
        id: 'billing',
        to: ROUTE_PATHS.billing,
        icon: LuCreditCard,
        labelKey: 'Billing',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'branding',
        to: ROUTE_PATHS.branding,
        icon: LuPalette,
        labelKey: 'Branding',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
      },
      {
        id: 'starter',
        to: ROUTE_PATHS.starterHome,
        icon: LuFlag,
        labelKey: 'Starter guide',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
        freeOnly: true,
      },
    ],
  },
  {
    id: 'admin',
    titleKey: 'Admin',
    showSectionHeader: true,
    items: [
      {
        id: 'users',
        to: '/users',
        icon: LuUsers,
        labelKey: 'Team',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        adminBadge: true,
        freeHidden: true,
      },
      {
        id: 'video-pipeline',
        to: ROUTE_PATHS.videoPipeline,
        icon: LuWorkflow,
        labelKey: 'Video Pipeline',
        scoped: true,
        roles: ['SuperAdmin'],
        adminBadge: true,
      },
    ],
  },
];

/**
 * @param {string} role
 * @param {string} [appEnv]
 * @param {string[]} [enabledFeatureKeys] lowercase feature keys that are on for the account
 * @param {{ plan?: string, isPremium?: boolean }} [saasPlan]
 * @returns {Array<{ id: string, titleKey: string, items: object[] }>}
 */
export function getVisibleSections(
  role,
  appEnv = process.env.REACT_APP_ENV || 'dev',
  enabledFeatureKeys = [],
  saasPlan = {}
) {
  const enabled = new Set(
    (enabledFeatureKeys || []).map((k) => String(k || '').toLowerCase()).filter(Boolean)
  );
  const isFree =
    !saasPlan?.isPremium &&
    String(saasPlan?.plan || 'free').toLowerCase() === 'free';
  return SIDEBAR_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (!item.roles.includes(role)) {
        return false;
      }
      if (item.envs && !item.envs.includes(appEnv)) {
        return false;
      }
      if (item.featureKey && !enabled.has(String(item.featureKey).toLowerCase())) {
        return false;
      }
      if (isFree && item.freeHidden) {
        return false;
      }
      if (item.freeOnly && !isFree) {
        return false;
      }
      return true;
    }),
  })).filter((section) => section.items.length > 0);
}
